"use client"

import { useEffect, useMemo, useRef } from "react"
import { Carousel } from "./carousel"
import gsap from "@/lib/gsap-init"
import { useVoyagesData } from "@/app/store/useVoyagesSharedData"
import { useTranslations, useLocale } from 'next-intl'
import { useLocalizedField } from '@/hooks/useLocalizedField'

export function CircuitsSection() {
  const t = useTranslations()
  const locale = useLocale()
  const { getLocalizedField } = useLocalizedField()
  const {
    voyages,        // ⬅️ les voyages récupérés depuis le store
    loading,
    error,
    fetchVoyages,   // ⬅️ fonction pour charger les voyages
  } = useVoyagesData()

  const sectionRef = useRef<HTMLDivElement>(null)
  const carouselRef = useRef<HTMLDivElement>(null)

  // 👉 Fetch voyages at component mount
  useEffect(() => {
    fetchVoyages()
  }, [fetchVoyages])

    const carouselItems = useMemo(() => {
      if (!voyages || voyages.length === 0) return []
  
      return voyages.map((d:any) => ({
        id: d.slug || d.name,
        name: `${d.number} ${t('common.days')}`,
        title: getLocalizedField(d, 'name'),
        description: getLocalizedField(d, 'description'),
        image: d.images?.[1] || d.images?.[0],
      }))
    }, [voyages, getLocalizedField, t])


  useEffect(() => {
    if (!sectionRef.current) return

    const ctx = gsap.context(() => {
      gsap.from(carouselRef.current, {
        opacity: 0,
        y: 60,
        duration: 0.8,
        scrollTrigger: {
          trigger: sectionRef.current,
          start: "top center",
          end: "center center",
          scrub: 0.5,
        },
      })
    }, sectionRef)

    return () => ctx.revert()
  }, [])

  return (
    <section id="circuits" ref={sectionRef} className="py-4 px-4 bg-card/30 transition-colors duration-300" dir={locale === 'ar' ? 'rtl' : 'ltr'}>
      <div className="max-w-7xl mx-auto">

        {/* Loading */}
        {loading && (
          <p className="text-muted-foreground text-lg">{t('common.loading')}</p>
        )}

        {/* Error */}
        {error && (
          <p className="text-red-500">{t('common.error')}</p>
        )}

        {/* Carousel */}
        {!loading && voyages.length > 0 && (
          <div ref={carouselRef}>
            <Carousel 
              items={carouselItems} 
              showItemsCount={4.5}
              title={t('navigation.circuits')}
              paragraph={t('sections.popularDestinations')}
              type="circuit"
            />
          </div>
        )}

      </div>
    </section>
  )
}
